import React, { useEffect, useRef, useState } from 'react';
import { obterLinkVigente } from '../../../api/portalCotacoes/portalCotacoesApi';
import { ConviteDetalhe, LinkVigente, StatusOctadesk } from '../../../interfaces/PortalCotacoes';
import { ConviteBadge } from './PortalBadges';
import { notify } from '../../../shared/feedback/notify';
// Overlay compartilhado dos modais do sistema (definido no AddCarModal e usado por 16+ modais).
// Import explícito para não depender de outro modal ter entrado no bundle antes deste.
import '../../../components/modal/add-car/AddCarModal.css';
import './ConviteAcoesModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * O convite de UM fornecedor: situação, envio, link e ações — at-07 (19/08/2026).
 *
 * ## Por que é modal, e não botões na linha
 *
 * Eram **cinco** botões dentro da célula, com `flex-wrap: wrap` — quebravam em duas linhas
 * (contra a regra do Victor de 12/08/2026) e nenhum tinha espaço para dizer o que fazia. A
 * distinção que mais importa era invisível: **"Reenviar" reaproveita o link vigente, "Gerar novo
 * link" INVALIDA o que o fornecedor está usando** — dois botões de aparência idêntica, um deles
 * destrutivo.
 *
 * O modal resolve os dois problemas de uma vez: a célula volta a ter um elemento, e as ações
 * ganham descrição. E, por ser modal, ele carrega o que não cabia em nenhuma célula — o link
 * vigente, o desfecho de cada canal com horário e motivo do erro, e o histórico de acesso.
 *
 * ## ⚠️ WhatsApp é UMA ação, exclusiva por estado da integração (at-08, 20/08/2026)
 *
 * Na at-07 as duas apareciam juntas quando a integração estava efetiva: "Enviar WhatsApp"
 * (servidor) e "WhatsApp manual" (wa.me), lado a lado. O Victor pediu para virar OU/OU — "se a
 * integração tiver ativa, o envio manual some (e vice-versa)". O gate continua sendo `efetiva`
 * (não `ativa`): switch ligado sem credencial ou sem modelo de mensagem não teria como enviar
 * pelo servidor, então nesse caso o único botão que aparece é o manual, que sempre funciona.
 *
 * Falha do envio ativo **não reintroduz o manual**: o toast orienta desativar a integração (ver
 * `PortalCotacaoDetalhe.tsx` → `enviarWhatsappPeloServidor`), porque reaparecer um botão manual
 * escondido no momento de erro seria voltar ao caminho duplo bem na hora da confusão.
 *
 * ## ⚠️ Toda ação FECHA o modal antes de executar
 *
 * Não é preferência de fluxo: "Reenviar" e "Gerar novo link" abrem o `ResultadoDisparoModal`, e
 * modal filho renderizado fora do `ref` do hospedeiro faz o handler de "clicou fora" do
 * hospedeiro tratar qualquer clique no filho como externo — a armadilha registrada em
 * `03-code-conventions.md`. Fechar antes elimina o caso por construção, e é o que a pessoa
 * espera: agiu, agora lê o resultado.
 *
 * ## Estilo
 *
 * Receita dos modais do módulo (`FornecedorDetalhesModal`): overlay compartilhado, cabeçalho com
 * título + fechar, corpo em seções com `h3`, classes namespeadas neste arquivo.
 */

interface ConviteAcoesModalProps {
  convite: ConviteDetalhe;
  /** Estado da integração de mensagens: decide se "Enviar WhatsApp" existe. */
  octadesk: StatusOctadesk | null;
  /** true enquanto a página processa uma ação — desabilita tudo. */
  processando: boolean;
  onFechar: () => void;
  onEnviarWhatsappServidor: () => void;
  onWhatsappManual: () => void;
  onReenviar: () => void;
  onGerarNovoLink: () => void;
  /** Manda ao fornecedor o resumo da proposta dele (at-16). Só existe com proposta e para quem age. */
  onEnviarResumo?: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

const ROTULO_CANAL: Record<string, string> = { EMAIL: 'E-mail', WHATSAPP: 'WhatsApp' };

function dataHora(valor: string): string {
  return new Date(valor).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ConviteAcoesModal: React.FC<ConviteAcoesModalProps> = ({
  convite,
  octadesk,
  processando,
  onFechar,
  onEnviarWhatsappServidor,
  onWhatsappManual,
  onReenviar,
  onGerarNovoLink,
  onEnviarResumo,
  sidebarRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [link, setLink] = useState<LinkVigente | null>(null);
  const [erroLink, setErroLink] = useState(false);
  const [carregandoLink, setCarregandoLink] = useState(true);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      const foraDoModal = modalRef.current && !modalRef.current.contains(alvo);
      const foraDaSidebar = !sidebarRef?.current || !sidebarRef.current.contains(alvo);
      if (foraDoModal && foraDaSidebar) onFechar();
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };

    document.addEventListener('mousedown', cliqueFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', cliqueFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [onFechar, sidebarRef]);

  /*
   * O link vigente é LEITURA PURA (ADR 0004): consultá-lo não invalida nada, então pode ser
   * carregado na abertura. Foi o que aposentou a ação "Ver link" — a informação passou a estar
   * no modal, e ação é ação.
   */
  useEffect(() => {
    let ativo = true;
    obterLinkVigente(convite.id)
      .then((r) => {
        if (!ativo) return;
        setLink(r);
        setErroLink(false);
      })
      .catch((error) => {
        if (!ativo) return;
        // Erro de API não pode virar afirmação de negócio (at-60): "sem link" e "não consegui
        // consultar o link" são coisas diferentes para quem lê.
        setErroLink(true);
        notify.errorFrom(error, 'Não foi possível consultar o link deste fornecedor.');
      })
      .finally(() => {
        if (ativo) setCarregandoLink(false);
      });
    return () => {
      ativo = false;
    };
  }, [convite.id]);

  const copiarLink = async () => {
    if (!link?.link) return;
    try {
      await navigator.clipboard.writeText(link.link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      notify.warning('Não foi possível copiar automaticamente. Selecione o link e copie.');
    }
  };

  /** Fecha antes de agir — ver o docblock (modal filho sobre hospedeiro). */
  const executar = (acao: () => void) => {
    onFechar();
    acao();
  };

  const disparos = convite.ultimosDisparos ?? [];

  return (
    <div className="modal-overlay">
      <div className="convite-modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Convite do fornecedor">
        <div className="convite-modal-header">
          <div className="convite-modal-titulo">
            <h2 title={convite.fornecedor.razaoSocial}>{convite.fornecedor.razaoSocial}</h2>
            <span className="convite-modal-cnpj">{convite.fornecedorCnpj}</span>
          </div>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="convite-modal-corpo">
          <section className="convite-modal-secao">
            <h3>Situação do convite</h3>
            <div className="convite-modal-grade">
              <span className="convite-modal-rotulo">Estado</span>
              <span>
                <ConviteBadge status={convite.status} />
              </span>

              <span className="convite-modal-rotulo">Link válido até</span>
              <span>{dataHora(convite.expiresAt)}</span>

              <span className="convite-modal-rotulo">Primeiro acesso</span>
              <span>{convite.primeiroAcessoEm ? dataHora(convite.primeiroAcessoEm) : 'ainda não abriu'}</span>

              <span className="convite-modal-rotulo">Resposta</span>
              <span>{convite.respondidoEm ? dataHora(convite.respondidoEm) : 'ainda não respondeu'}</span>
            </div>
          </section>

          <section className="convite-modal-secao">
            <h3>Envio</h3>
            {/*
              Aqui, ao contrário da célula da tabela, cada canal tem a sua linha e o texto
              inteiro — é o espaço que a tabela não tem. O `estado` é o mesmo da coluna "Envio".
            */}
            {disparos.length === 0 ? (
              <p className="convite-modal-vazio">
                Nenhum envio registrado para este convite. Canais previstos:{' '}
                {convite.canais.map((c) => ROTULO_CANAL[c] ?? c).join(', ') || 'nenhum'}.
              </p>
            ) : (
              <ul className="convite-modal-envios">
                {disparos.map((d) => {
                  const rotulo = ROTULO_CANAL[d.canal] ?? d.canal;
                  const classe = !d.sucesso
                    ? 'convite-envio--falhou'
                    : d.manual
                      ? 'convite-envio--manual'
                      : 'convite-envio--ok';
                  const estado = !d.sucesso ? 'falhou' : d.manual ? 'a enviar' : 'enviado';
                  return (
                    <li key={d.canal} className="convite-modal-envio">
                      <span className="convite-modal-envio-canal">{rotulo}</span>
                      <span className={`convite-modal-envio-estado ${classe}`}>{estado}</span>
                      <span className="convite-modal-envio-quando">{dataHora(d.em)}</span>
                      {!d.sucesso && d.erro && <span className="convite-modal-envio-motivo">{d.erro}</span>}
                      {d.sucesso && d.manual && (
                        <span className="convite-modal-envio-motivo">
                          O sistema não envia esta mensagem — quem envia é você, pela ação de WhatsApp abaixo.
                        </span>
                      )}
                      {d.sucesso && !d.manual && d.provedorMensagemId && (
                        <span className="convite-modal-envio-motivo">
                          Mensagem na plataforma: {d.provedorMensagemId}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="convite-modal-secao">
            <h3>Link do fornecedor</h3>
            {carregandoLink && <p className="convite-modal-vazio">Consultando o link...</p>}
            {!carregandoLink && erroLink && (
              <p className="convite-modal-erro">
                Não foi possível consultar o link agora. Isso não significa que o fornecedor esteja sem link —
                tente novamente ou recarregue a página.
              </p>
            )}
            {!carregandoLink && !erroLink && link && (
              <>
                {link.disponivel && link.link ? (
                  <div className="convite-modal-link">
                    <code title={link.link}>{link.link}</code>
                    <button type="button" className="pc-btn pc-btn--secundario pc-btn--sm" onClick={copiarLink}>
                      {copiado ? 'Copiado' : 'Copiar'}
                    </button>
                  </div>
                ) : (
                  <p className="convite-modal-vazio">
                    Este convite foi criado antes de o sistema guardar o link para consulta, então não é possível
                    exibi-lo. O link que o fornecedor recebeu continua valendo
                    {link.prefixo ? ` (começa com "${link.prefixo}")` : ''}. Se ele não tiver mais o link, use
                    "Gerar novo link" — o atual deixará de valer.
                  </p>
                )}
              </>
            )}
          </section>

          <section className="convite-modal-secao">
            <h3>Ações</h3>
            <div className="convite-modal-acoes">
              {/*
                ⚠️ EXCLUSIVO por estado da integração (at-08, fix de 20/08/2026): antes as duas
                ações de WhatsApp coexistiam ("Enviar WhatsApp" e "WhatsApp manual" lado a lado),
                e o Victor pediu para virar OU/OU — "se a integração tiver ativa, o envio manual
                some (e vice-versa)".

                O gate é `efetiva`, não `ativa`: switch ligado sem credencial ou sem modelo de
                mensagem não envia nada, e a ação prometeria o que não acontece (409 do
                backend) — nesse caso o manual continua sendo o único caminho que funciona.
              */}
              {octadesk?.efetiva ? (
                <button
                  type="button"
                  className="convite-modal-acao"
                  disabled={processando}
                  onClick={() => executar(onEnviarWhatsappServidor)}
                >
                  <span className="convite-modal-acao-rotulo">Enviar WhatsApp</span>
                  <span className="convite-modal-acao-descricao">
                    O sistema envia agora, pelo número oficial da empresa. Mantém o link atual.
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  className="convite-modal-acao"
                  disabled={processando}
                  onClick={() => executar(onWhatsappManual)}
                >
                  <span className="convite-modal-acao-rotulo">WhatsApp</span>
                  <span className="convite-modal-acao-descricao">
                    Abre a conversa com a mensagem pronta — quem envia é você. Gera um link novo.
                  </span>
                </button>
              )}

              <button
                type="button"
                className="convite-modal-acao"
                disabled={processando}
                onClick={() => executar(onReenviar)}
              >
                <span className="convite-modal-acao-rotulo">Reenviar por e-mail</span>
                <span className="convite-modal-acao-descricao">
                  Dispara o convite outra vez, com o mesmo link.
                </span>
              </button>

              <button
                type="button"
                className="convite-modal-acao convite-modal-acao--perigo"
                disabled={processando}
                onClick={() => executar(onGerarNovoLink)}
              >
                <span className="convite-modal-acao-rotulo">Gerar novo link</span>
                <span className="convite-modal-acao-descricao">
                  Invalida o link que o fornecedor recebeu e cria outro. Use quando o link foi para o contato
                  errado.
                </span>
              </button>

              {onEnviarResumo && (
                <button
                  type="button"
                  className="convite-modal-acao"
                  disabled={processando}
                  onClick={() => executar(onEnviarResumo)}
                >
                  <span className="convite-modal-acao-rotulo">Enviar resumo da proposta</span>
                  <span className="convite-modal-acao-descricao">
                    Manda por e-mail ao fornecedor tudo o que ele respondeu, para ele conferir.
                  </span>
                </button>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ConviteAcoesModal;
